import classNames from 'classnames';
import React, { useCallback } from 'react';

import {
  FormControl,
  FormHelperText,
  InputAdornment,
  Input as MuiInput,
  makeStyles,
} from '@material-ui/core';

import { useErrorStyles } from './form.styles';
import { LabelBlock } from './index';

const useStyles = makeStyles((theme) => ({
  input: {
    '& label': {
      transform: 'translate(0, 1.5px)',
      width: '100%',
      '& .label--required': {
        color: theme.palette.primary.main,
      },
      '& .label--icon-valid': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiInput-root': {
      backgroundColor: '#ffffff',
      border: '1px solid #999999',
      borderRadius: 4,
      marginTop: '2rem',
      padding: '2px 6px',
    },
    '& .MuiInput-inputMultiline': {
      paddingTop: 4,
    },
  },
}));

type Props = {
  name?: string;
  label: string;
  type?: 'text' | 'number';
  value?: string | number;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  tooltip?: string;
  iconTooltip?: React.ReactNode;
  error?: string;
  touched?: boolean;
  required?: boolean;
  regex?: RegExp;
  isErrorVisible?: boolean;
  autoFocus?: boolean;
  dataHjAllow?: boolean;
  onFocus?: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
  onChange?: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
  onBlur?: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
  innerRef?: React.MutableRefObject<HTMLInputElement | null>;
};
export const Input: React.FC<Props> = (props) => {
  const {
    label,
    type = 'text',
    value = '',
    placeholder,
    rows,
    multiline = false,
    prefix,
    suffix,
    tooltip,
    iconTooltip,
    error,
    regex,
    onChange,
    onBlur,
    touched,
    isErrorVisible,
    required = true,
    innerRef,
    autoFocus,
    dataHjAllow,
    onFocus,
  } = props;
  const name = props.name ?? props.label;
  const classes = useStyles();
  const { errorFont, errorBlock } = useErrorStyles();

  const onChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (regex && !regex.test(e?.target.value)) return;
      onChange?.(e);
    },
    [regex, onChange],
  );

  return (
    <FormControl fullWidth className={classes.input}>
      <LabelBlock
        label={label}
        required={required}
        tooltip={tooltip}
        iconTooltip={iconTooltip}
        error={error}
        touched={touched}
        isErrorVisible={isErrorVisible}
      />
      <MuiInput
        name={name}
        type={type}
        value={value}
        ref={innerRef}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onChange={(e) => onChangeInput?.(e)}
        onBlur={(e) => onBlur?.(e)}
        inputProps={{ 'data-hj-allow': dataHjAllow }}
        {...(multiline && { multiline })}
        {...(rows && { rows })}
        {...(placeholder && { placeholder })}
        {...(prefix && {
          startAdornment: (
            <InputAdornment position="start">{prefix}</InputAdornment>
          ),
        })}
        {...(suffix && {
          endAdornment: (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ),
        })}
      />
      {error && (touched || isErrorVisible) && (
        <FormHelperText className={classNames(errorFont, errorBlock)}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};
